import { S25Util } from "../../util/s25-util";
import {
    ChangeDetectorRef,
    Component,
    OnInit,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    Input,
    Output,
    EventEmitter,
    ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { AiAdvancedSearchResponse, Message, S25ChatbotService } from "./s25.chatbot.service";
import { S25ChatbotComponent } from "./s25.chat.bot.component";

@TypeManagerDecorator("s25-ng-ai-search-chatbot")
@Component({
    selector: "s25-ng-ai-search-chatbot",
    template: `
        @if (init) {
            <s25-ng-ai-chatbot
                #chatbot
                [welcomeMessage]="
                    'Provide details on what you are looking for and I can help you get started on a SeriesQL query.'
                "
                [isLoading]="isLoading"
                (messageThreadSent)="handleMessageThreadSent($event)"
                [resetOnClose]="true"
            ></s25-ng-ai-chatbot>
        }
    `,
    styles: ``,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, S25ChatbotComponent],
    standalone: true,
})
export class S25AiSearchChatbotComponent implements OnInit {
    @Input() onChange: (query: AiAdvancedSearchResponse) => AiAdvancedSearchResponse = undefined;
    @Output() onChatResponse = new EventEmitter<AiAdvancedSearchResponse>();

    @ViewChild("chatbot") chatbot!: S25ChatbotComponent;

    init: boolean = false;
    isLoading: boolean = true;
    currentQuerySegment: number = 0;
    querySegments: Array<string> = [];

    chatbotService: S25ChatbotService<AiAdvancedSearchResponse> = new S25ChatbotService<AiAdvancedSearchResponse>(
        "advancedSearch",
    );

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.init = true;
        this.isLoading = false;
        this.cd.detectChanges();
    }

    splitQueryIntoSegments(query: string): string[] {
        const regex = /\(.*?\)/g;
        const segments: string[] = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = regex.exec(query)) !== null) {
            const endIndex = match.index + match[0].length;
            const segment = query.slice(lastIndex, endIndex);
            segments.push(segment);
            lastIndex = endIndex;
        }

        // Optionally handle remaining content after the last placeholder
        const remaining = query.slice(lastIndex);
        if (remaining) {
            segments.push(remaining);
        }
        return segments;
    }

    hasParentheses(query: string): boolean {
        return /\([^()]*\)/.test(query);
    }

    hasMoreSegments(): boolean {
        return this.querySegments.length && this.currentQuerySegment <= this.querySegments.length - 1;
    }

    incrementQuerySegment() {
        this.currentQuerySegment += 1;
    }

    async handleMessageThreadSent(thread: Message[]) {
        this.isLoading = true;
        this.currentQuerySegment = 0;
        this.querySegments = [];

        try {
            // Await AI response
            const resp = await this.chatbotService.sendMessageThread(thread);
            if (!resp) {
                this.chatbot.addResponse("An error has occurred. Please try again.");
            }
            if (resp.chatResponse && !resp.query) {
                this.chatbot.addResponse(resp.chatResponse);
            }
            if (resp.query) {
                if (!resp.query.startsWith("::")) {
                    resp.query = "::" + resp.query;
                }
                this.querySegments = this.splitQueryIntoSegments(resp.query);
                if (this.hasParentheses(resp.query)) {
                    this.chatbot.addResponse(
                        `A query has been generated and broken into ${this.querySegments.length} segment(s). Please fill out the placeholder values one at a time until the query has completed populating.`,
                    );
                }
            }
            this.isLoading = false;
            this.onChatResponse.emit(resp);
        } catch {
            this.chatbot.addResponse("An error has occurred. Please try again.");
        }
    }
}
